import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Box,
  Grid,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import ViewListIcon from '@mui/icons-material/ViewList';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { HeaderSubTitle, ProjectRow, ProjectTableCell } from './styles/plannedVsActualReportStyles';
import { TaskView } from './TaskView';
import { EmployeeView } from './EmployeeView';
import { formatSecondesToHourMinute } from '../../../../utils/formatTime';
import Iconify from '../../../../components/iconify';
import { StatusChip } from './StatusChip';
import { PlannedVsActualReportExcelExport } from './excelExport';
import NoDataComponent from '../../../../common/NoDataComponent';

export default function PlannedVsActualReportDataTable({ data, startDate, endDate }) {
  const isEmpty = data?.length === 0;
  const [viewMode, setViewMode] = useState('tasks');
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [expandedTasks, setExpandedTasks] = useState(new Set());

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const expandAll = () => {
    setExpandedProjects(new Set(data.map((_, index) => index)));
    setExpandedEmployees(new Set(data.flatMap((project) => project.members?.map((member) => member.id) || [])));
    setExpandedTasks(
      new Set(
        data.flatMap(
          (project) =>
            project.members?.flatMap(
              (member) => member.tasks?.map((_, taskIndex) => `${member.id}-${taskIndex}`) || []
            ) || []
        )
      )
    );
  };

  const collapseAll = () => {
    setExpandedProjects(new Set());
    setExpandedEmployees(new Set());
    setExpandedTasks(new Set());
  };

  const toggleProject = (index) => {
    const newSet = new Set(expandedProjects);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setExpandedProjects(newSet);
  };

  const toggleEmployee = (id) => {
    const newSet = new Set(expandedEmployees);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedEmployees(newSet);
  };

  const toggleTask = (id) => {
    const newSet = new Set(expandedTasks);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedTasks(newSet);
  };

  return (
    <Box sx={{ width: '100%', mt: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Grid item xs={12} className="report-heading" sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
          Planned vs Actual Report
          {!isEmpty && (
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewChange} aria-label="view mode">
                <ToggleButton value="tasks" aria-label="tasks view">
                  <AssignmentIcon sx={{ mr: 1 }} />
                  Tasks
                </ToggleButton>
                <ToggleButton value="employees" aria-label="employees view">
                  <ViewListIcon sx={{ mr: 1 }} />
                  Employees
                </ToggleButton>
              </ToggleButtonGroup>
              <Button variant="outlined" startIcon={<KeyboardArrowDown />} onClick={expandAll}>
                Expand All
              </Button>
              <Button variant="outlined" startIcon={<KeyboardArrowRight />} onClick={collapseAll}>
                Collapse All
              </Button>

              <Button
                variant="outlined"
                startIcon={<TextSnippetIcon />}
                onClick={() => PlannedVsActualReportExcelExport(data, startDate, endDate)}
              >
                Export Excel
              </Button>
            </Box>
          )}
        </Grid>
      </Box>
      {isEmpty ? (
        <NoDataComponent />
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 2,
            overflow: 'hidden',
            '& .MuiTable-root': {
              minWidth: '100%',
              overflow: 'hidden',
            },
          }}
        >
          <Table
            sx={{
              tableLayout: 'fixed',
              '& .MuiCollapse-root': {
                overflow: 'hidden',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '50px' }} />
                <TableCell sx={{ width: '30%' }}>Project Name</TableCell>
                <TableCell sx={{ width: '10%' }}>Status</TableCell>
                <TableCell sx={{ width: '10%' }} align="right">
                  Progress
                </TableCell>
                <TableCell sx={{ width: '25%' }} align="right">
                  Consumed Efforts
                  {startDate && endDate && (
                    <HeaderSubTitle>
                      {format(new Date(startDate), 'dd-MM-yyyy')} to {format(new Date(endDate), 'dd-MM-yyyy')}
                    </HeaderSubTitle>
                  )}
                </TableCell>
                <TableCell sx={{ width: '25%' }} align="right">
                  Planned Efforts
                  {startDate && endDate && (
                    <HeaderSubTitle>
                      {format(new Date(startDate), 'dd-MM-yyyy')} to {format(new Date(endDate), 'dd-MM-yyyy')}
                    </HeaderSubTitle>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((project, projectIndex) => (
                <React.Fragment key={project.projectName}>
                  <ProjectRow>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleProject(projectIndex)}>
                        {expandedProjects.has(projectIndex) ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                      </IconButton>
                    </TableCell>
                    <ProjectTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Iconify icon="ant-design:project-filled" />
                        <Typography variant="subtitle1" noWrap>
                          {project.projectName}{' '}
                        </Typography>
                        {viewMode === 'tasks' ? (
                          <StatusChip
                            status={project.tasks?.length || 0}
                            type="count"
                            label={`${project.tasks?.length} tasks`}
                          />
                        ) : (
                          <StatusChip
                            status={project.members?.length || 0}
                            type="count"
                            label={`${project.members?.length} employees`}
                          />
                        )}
                      </Box>
                    </ProjectTableCell>

                    <ProjectTableCell>
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </ProjectTableCell>
                    <ProjectTableCell align="right">
                      <StatusChip
                        status={
                          Math.round(
                            ((project.totalConsumedSeconds || 0) / (project.totalPlannedSeconds || 1)) * 100
                          ) || 0
                        }
                        type="progress"
                      />
                    </ProjectTableCell>
                    {/* <ProjectTableCell align="right">
                      {formatSecondesToHourMinute(project.periodConsumedSeconds || 0)}
                    </ProjectTableCell> */}
                    <ProjectTableCell align="right">
                      {formatSecondesToHourMinute(project.totalConsumedSeconds || 0)}
                    </ProjectTableCell>
                    <ProjectTableCell align="right">
                      {formatSecondesToHourMinute(project.totalPlannedSeconds || 0)}
                    </ProjectTableCell>
                  </ProjectRow>

                  {viewMode === 'tasks' ? (
                    <TaskView
                      project={project}
                      projectIndex={projectIndex}
                      isExpanded={expandedProjects.has(projectIndex)}
                      expandedTasks={expandedTasks}
                      onToggleTask={toggleTask}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  ) : (
                    <EmployeeView
                      project={project}
                      projectIndex={projectIndex}
                      isExpanded={expandedProjects.has(projectIndex)}
                      expandedEmployees={expandedEmployees}
                      expandedTasks={expandedTasks}
                      onToggleEmployee={toggleEmployee}
                      onToggleTask={toggleTask}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

PlannedVsActualReportDataTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      projectName: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      periodConsumedSeconds: PropTypes.number,
      totalConsumedSeconds: PropTypes.number,
      members: PropTypes.array,
      tasks: PropTypes.array,
    })
  ).isRequired,
  startDate: PropTypes.instanceOf(Date).isRequired,
  endDate: PropTypes.instanceOf(Date).isRequired,
};
